/*
 *
 * FundShow
 *
 */
import {
  Grid,
  Typography,
  Hidden,
  Button,
  makeStyles,
  Box,
  Link as MUILink,
} from '@material-ui/core'
import LaunchIcon from '@material-ui/icons/Launch'
import { Alert } from '@material-ui/lab'
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import classnames from 'classnames'
import Head from 'next/head'
import Image from 'next/image'
import React, { useState } from 'react'

import ButtonLink from '@aletheia/common/components/ButtonLink'
import { FAQEntryEAFunds } from '@aletheia/common/components/FAQEntry'
import HR from '@aletheia/common/components/HR'
import Link from '@aletheia/common/components/Link'
import Markdown from '@aletheia/common/components/Markdown'
import { FundPayoutReportList } from '@aletheia/common/components/__legacy/FundPayoutReports'
import { isNotLink } from '@aletheia/common/lib/contentful/utils'
import {
  IFund,
  IPayoutReport,
} from '@aletheia/eafunds/contentful/types/generated'

import { GWWC_DONATE_URL } from '../../globals/routes'
import { fundKey } from '../../lib/grantsDB'
import { Grant } from '../../pages/grants'
import { StaffBoxes } from '../../pages/team'
import {
  FundingChart,
  InstitutionalFundingData,
} from '../Statistics/InstitutionalFunding'
import { PayoutChartDataPoint, PayoutsChart } from '../Statistics/PayoutsByFund'
import { Advisors } from './FundManager'
import FundsShowSection from './FundsShowSection'
import { HighlightedGrants } from './HighlightedGrants'
import TOC from './TOC'
import { getMarkdownFields } from './utils'

const SLUG_TO_URL = {
  'far-future': '/charities/long-term-future-fund',
  'ea-community': '/charities/effective-altruism-infrastructure-fund',
  'animal-welfare': '/charities/animal-welfare-fund',
  'global-development': '/charities/global-health-and-development-fund',
}

type FundSlug = keyof typeof SLUG_TO_URL

const useFundGlobalActionsStyles = makeStyles((theme) => ({
  sidebarTitle: {
    overflow: 'hidden',
    maxHeight: 0,
    transition: 'max-height 0.6s',
    '&.show-title': {
      maxHeight: 320,
    },
  },
  sidebarTitleTypography: {
    fontSize: '1.3rem', // small enough to prevent word-wrap
    marginBottom: theme.spacing(2),
  },
}))

const FundGlobalActions: React.FC<{
  Fund: IFund['fields']
  sidebar?: boolean
  className?: string
}> = ({ Fund, sidebar = false, className }) => {
  const classes = useFundGlobalActionsStyles()
  const [showSidebarTitle, setShowSidebarTitle] = useState(false)

  useScrollPosition(
    ({ currPos }) => {
      const THRESHOLD = 320
      // currPos tells us how far above the viewport the top of the document is
      // so we test for below the negative threshold
      const pastThreshold = currPos.y < -THRESHOLD
      setShowSidebarTitle(pastThreshold)
    },
    [setShowSidebarTitle],
    undefined,
    undefined,
    500,
  )

  return (
    <div className={className}>
      {sidebar && (
        <div
          className={classnames(classes.sidebarTitle, {
            'show-title': showSidebarTitle,
          })}
        >
          <Typography
            variant="h5"
            component="h2"
            className={classes.sidebarTitleTypography}
          >
            {Fund.title} Fund
          </Typography>
        </div>
      )}
      <Box marginBottom={2}>
        <ButtonLink
          href={`${GWWC_DONATE_URL}${
            SLUG_TO_URL[Fund.slug as FundSlug]
          }?utm_source=eafunds`}
          fullWidth
          variant="contained"
          color="primary"
        >
          {sidebar ? 'Donate' : 'Make a donation'} &nbsp;
          <Image
            src="/logos/Giving What We Can - White.svg"
            alt="Giving What We Can Logo"
            width={32}
            height={32}
          ></Image>
        </ButtonLink>
      </Box>
      {Fund.applicationLink && (
        <Box marginBottom={2}>
          <Button
            fullWidth
            variant="contained"
            href={Fund.applicationLink}
            target="_blank"
            rel="noopener"
          >
            Apply for funding &nbsp;
            <LaunchIcon />
          </Button>
        </Box>
      )}
      {/* TOC */}
      <TOC Fund={Fund} sidebar={sidebar} />
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    [theme.breakpoints.down('sm')]: {
      overflowWrap: 'break-word',
      maxWidth: '100%',
    },
  },
  sidebarContainer: {
    position: 'sticky',
    top: theme.spacing(12),
    maxHeight: `calc(100vh - ${theme.spacing(12 + 6)}px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  shortDescriptionWrapper: {},
}))

const FundsShow: React.FC<{
  Fund: IFund['fields']
  PayoutReports: IPayoutReport[]
  payoutChartData: PayoutChartDataPoint[]
  fundingSourceChartData: InstitutionalFundingData
  highlightedGrants: Grant[]
}> = ({
  Fund,
  PayoutReports,
  payoutChartData,
  fundingSourceChartData,
  highlightedGrants,
}) => {
  const classes = useStyles()

  const markdownFields = getMarkdownFields(Fund)

  return (
    <div className={classes.root}>
      <Head>{`${Fund.title} Fund – EA Funds`}</Head>
      <Grid container spacing={4}>
        <Hidden smDown>
          <Grid item md={4}>
            <div className={classes.sidebarContainer}>
              <FundGlobalActions Fund={Fund} sidebar />
            </div>
          </Grid>
        </Hidden>

        <Grid item md={8} className={classes.content}>
          <FundsShowSection title="Basic Info" anchor="basic-info">
            {/* Basic info */}
            <div className={classes.shortDescriptionWrapper}>
              <Markdown>{Fund.shortDescription}</Markdown>
            </div>
            <Hidden mdUp>
              <FundGlobalActions Fund={Fund} />
            </Hidden>

            {/* Risk Profile */}
            <Box marginTop={3}>
              <Alert severity="info">
                <strong>Risk profile:</strong> {Fund.riskProfile}{' '}
                <Link href={`/risk`}>(learn more)</Link>
              </Alert>
            </Box>
          </FundsShowSection>

          {/* Highlighted Grants */}
          <FundsShowSection
            title="Highlighted Grants"
            anchor="highlighted-grants"
          >
            <HighlightedGrants
              grants={highlightedGrants}
              fundSlug={Fund.slug}
            />
          </FundsShowSection>

          {/* Text fields */}
          {markdownFields.map(({ id, name }) => {
            const markdownData = Fund[id]
            if (typeof markdownData !== 'string') return null
            return (
              <FundsShowSection key={id} title={name} anchor={id}>
                {id === 'cons' && (
                  <Typography gutterBottom>
                    <em>
                      We think it’s important that donors are well informed when
                      they donate to EA Funds. As such, we think it’s useful to
                      think about the reasons that you might choose to donate
                      elsewhere.
                    </em>
                  </Typography>
                )}
                <Markdown>{markdownData}</Markdown>
                {id === 'objects' && (
                  <Typography align="center" gutterBottom>
                    <Link href="/scope-and-limitations">
                      Read more about Fund scope and limitations
                    </Link>
                  </Typography>
                )}
                {id === 'grantmakingAndImpact' && (
                  <Typography align="center" gutterBottom>
                    <em>
                      For more information, please check the full list of the{' '}
                      <MUILink href={`#payout-reports`}>
                        {Fund.shortTitle || Fund.title} Fund’s Payout Reports
                      </MUILink>
                      .
                    </em>
                  </Typography>
                )}
              </FundsShowSection>
            )
          })}

          {/* Payout stats */}
          <FundsShowSection title="Payouts Over Time" anchor="payout-stats">
            <PayoutsChart
              data={payoutChartData}
              showLines={{ [Fund.slug.replace('-', '_')]: true }}
            />
          </FundsShowSection>

          {/* Payout reports */}
          <FundsShowSection title="Payout Reports" anchor="payout-reports">
            <Typography gutterBottom>
              <em>
                Note: Public payout reports are{' '}
                <Link href="https://forum.effectivealtruism.org/posts/LKdtHdETxSYAXwoW6/public-reports-are-now-optional-for-ea-funds-grantees">
                  optional
                </Link>{' '}
                for grantees of this fund, and we have{' '}
                <Link href="https://forum.effectivealtruism.org/posts/agaudjzbp5eBSnbom/ea-funds-has-a-public-grants-database">
                  discontinued payout reports
                </Link>
                .
              </em>
            </Typography>
            <FundPayoutReportList PayoutReports={PayoutReports} />
          </FundsShowSection>

          {/* Funding Sources */}
          <FundsShowSection
            title={`Funding Sources (${fundingSourceChartData.mostRecentYear})`}
            anchor="funding-sources"
          >
            <Typography gutterBottom>
              We get some of our funding from{' '}
              <Link href="/stats/funding-sources">institutional donors</Link>.
            </Typography>

            <FundingChart
              data={
                fundingSourceChartData.fundingByFund[
                  Fund.slug.replace('-', '_') as fundKey
                ]
              }
            />
          </FundsShowSection>

          {/* FAQ */}
          {Fund.faqEntries && !!Fund.faqEntries.length && (
            <FundsShowSection
              title={`${Fund.shortTitle || Fund.title} Fund FAQ`}
              anchor="faq"
            >
              {Fund.faqEntries.filter(isNotLink).map((FAQEntry) => (
                <React.Fragment key={FAQEntry.sys.id}>
                  <FAQEntryEAFunds FAQEntry={FAQEntry} noTags noAnchor />
                  <HR />
                </React.Fragment>
              ))}
              <Typography align="center" gutterBottom>
                <em>
                  For more information about EA Funds in general, see our{' '}
                  <Link href={`/faq`}>FAQ page.</Link>
                </em>
              </Typography>
            </FundsShowSection>
          )}

          {/* Fund Managers */}
          <FundsShowSection
            title={`Fund Manager${`${Fund.managers.length > 1 ? 's' : ''}`}`}
            anchor="fund-managers"
          >
            <StaffBoxes Fund={Fund} />
          </FundsShowSection>
          {/* Advisors */}
          {Fund.advisors && (
            <FundsShowSection
              title={`Fund Advisor${`${Fund.advisors.length > 1 ? 's' : ''}`}`}
              anchor="advisors"
            >
              <Advisors Fund={Fund} />
            </FundsShowSection>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

export default FundsShow
